import { Nullable } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { IRelation } from "../contracts/base/relation";
import { RelationCardinality } from "../contracts/base/relation-cardinality";
import { IRelationDefinition } from "../contracts/base/relation-definition";
import { RelationRole } from "../contracts/base/relation-role";
import {
    ChildToManyParentsRelation,
    IChildToManyParentsRelation,
} from "../contracts/base/relations/child-to-many-parents-relation";
import {
    ChildToOneParentRelation,
    IChildToOneParentRelation,
} from "../contracts/base/relations/child-to-one-parent-relation";
import {
    IParentToManyChildrenRelation,
    ParentToManyChildrenRelation,
} from "../contracts/base/relations/parent-to-many-children-relation";
import {
    IParentToOneChildRelation,
    ParentToOneChildRelation,
} from "../contracts/base/relations/parent-to-one-child-relation";
import Guard from "../guard";

export interface IRelationFactory {
    /**
     * Creates a {@link IChildToManyParentsRelation}.
     * @param definition - The relation definition
     * @returns A new relation.
     */
    createChildToManyParentsRelation(definition: IRelationDefinition): IChildToManyParentsRelation;

    /**
     * Creates a {@link IChildToOneParentRelation}.
     * @param definition - The relation definition
     * @returns A new relation.
     */
    createChildToOneParentRelation(definition: IRelationDefinition): IChildToOneParentRelation;

    /**
     * Creates a {@link IParentToManyChildrenRelation}.
     * @param definition - The relation definition
     * @returns A new relation.
     */
    createParentToManyChildrenRelation(definition: IRelationDefinition): IParentToManyChildrenRelation;

    /**
     * Creates a {@link IParentToOneChildRelation}.
     * @param definition - The relation definition
     * @returns A new relation.
     */
    createParentToOneChildRelation(definition: IRelationDefinition): IParentToOneChildRelation;

    /**
     * Creates a {@link IRelation}.
     * @param definition - The relation definition
     * @returns A new relation.
     */
    createRelation(definition: IRelationDefinition): IRelation;

    /**
     * Creates a new {@link IRelation} collection.
     * @param definition - The relation definition
     * @returns A collection of relations.
     */
    createRelations(definitions: Array<IRelationDefinition>): Array<IRelation>;
}

export class RelationFactory implements IRelationFactory {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        this._client = client;
    }

    createChildToManyParentsRelation(definition: IRelationDefinition): IChildToManyParentsRelation {
        const relation = new ChildToManyParentsRelation(definition.name, null, this._client);
        return relation;
    }

    createChildToOneParentRelation(definition: IRelationDefinition): IChildToOneParentRelation {
        const relation = new ChildToOneParentRelation(definition.name, null, this._client);
        return relation;
    }

    createParentToManyChildrenRelation(definition: IRelationDefinition): IParentToManyChildrenRelation {
        const relation = new ParentToManyChildrenRelation(definition.name, null, this._client);
        return relation;
    }

    createParentToOneChildRelation(definition: IRelationDefinition): IParentToOneChildRelation {
        const relation = new ParentToOneChildRelation(definition.name, null, this._client);
        return relation;
    }

    createRelation(definition: IRelationDefinition): IRelation {
        Guard.notNullOrUndefined(definition);

        let result: Nullable<IRelation> = null;

        if (definition.role === RelationRole.Parent) {
            if (
                definition.cardinality === RelationCardinality.ManyToMany ||
                definition.cardinality === RelationCardinality.OneToMany
            ) {
                result = this.createParentToManyChildrenRelation(definition);
            } else if (definition.cardinality === RelationCardinality.OneToOne) {
                result = this.createParentToOneChildRelation(definition);
            }
        } else if (definition.role === RelationRole.Child) {
            if (definition.cardinality === RelationCardinality.ManyToMany) {
                result = this.createChildToManyParentsRelation(definition);
            } else if (
                definition.cardinality === RelationCardinality.OneToOne ||
                definition.cardinality === RelationCardinality.OneToMany
            ) {
                result = this.createChildToOneParentRelation(definition);
            }
        }

        if (result === null) {
            throw `Relation with name '${definition.name}', cardinality '${definition.cardinality}' and role '${definition.role}' not supported.`; //TODO
        }

        return result;
    }

    createRelations(definitions: Array<IRelationDefinition>): Array<IRelation> {
        if (!definitions || !definitions.length) {
            return [];
        }

        const list: Array<IRelation> = definitions.map(definition => {
            return this.createRelation(definition);
        });

        return list;
    }
}
